import { getRenderableMessageText, parseAssistantErrorMessage } from '../messageText';
import { computed, createModel, signal } from '@preact/signals-react';
import type { ChatStatus, UIMessage } from 'ai';

export type AssistantRenderableMessage = {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    isStreaming: boolean;
};

export type AssistantChatStateInit = {
    messages?: UIMessage[];
    status?: ChatStatus;
    error?: Error;
};

const isRenderableRole = (role: string): role is 'user' | 'assistant' => role === 'user' || role === 'assistant';

export const AssistantChatStateModel = createModel((init: AssistantChatStateInit = {}) => {
    const messages = signal<UIMessage[]>(init.messages ?? []);
    const status = signal<ChatStatus>(init.status ?? 'ready');
    const error = signal<Error | undefined>(init.error);

    const isLoading = computed(() => status.value === 'submitted' || status.value === 'streaming');
    const errorMessage = computed(() => parseAssistantErrorMessage(error.value));
    const renderableMessages = computed<AssistantRenderableMessage[]>(() => {
        const lastMessage = messages.value.at(-1);

        return messages.value.flatMap((message) => {
            if (!isRenderableRole(message.role)) return [];

            const text = getRenderableMessageText(message.parts);
            if (!text) return [];

            return [
                {
                    id: message.id,
                    role: message.role,
                    text,
                    isStreaming: isLoading.value && message.role === 'assistant' && message.id === lastMessage?.id,
                },
            ];
        });
    });
    const showLoadingPlaceholder = computed(() => {
        const lastMessage = messages.value.at(-1);
        const lastAssistantText = lastMessage?.role === 'assistant' ? getRenderableMessageText(lastMessage.parts) : '';
        return isLoading.value && !lastAssistantText.trim();
    });

    return {
        messages,
        status,
        error,
        isLoading,
        errorMessage,
        renderableMessages,
        showLoadingPlaceholder,
        setMessages(nextMessages: UIMessage[]): void {
            messages.value = nextMessages;
        },
        setStatus(nextStatus: ChatStatus): void {
            status.value = nextStatus;
        },
        setError(nextError: Error | undefined): void {
            error.value = nextError;
        },
    };
});

export type AssistantChatStateModel = InstanceType<typeof AssistantChatStateModel>;
